import React from 'react';
import _ = require('underscore');

import { TextLinkKind, TextLinkWidget, Tooltip, TooltipPosition } from '@tableau/widgets';

import { BrowserTitleService } from '~commonInfra/services/BrowserTitleService';
import { HelpService } from '~commonInfra/services/HelpService';
import { WindowLocationService } from '~commonInfra/services/WindowLocationService';
import { Helpers } from '~commonInfra/utils/Helpers';
import { Keys } from '~commonInfra/utils/Keys';
import { RescheduleIdentityMigrationJobButton } from '~src/authentication/components/userIdentitiesSpace/RescheduleIdentityMigrationJobButton';
import { IdentityMigrationFailureType, localizeIdentityMigrationFailureGroupHeader } from '~src/authentication/models/IdentityMigrationFailureType';
import { GetUserIdentityConflictTotalsResult } from '~src/authentication/serverApi/requests/GetUserIdentityConflictTotalsRequest';
import { GetUserIdentityMigrationProgressResult } from '~src/authentication/serverApi/requests/GetUserIdentityMigrationProgressRequest';
import { UserIdentityConflictsService } from '~src/authentication/services/UserIdentityConflictsService';
import { AllUserIdentitiesSubspaceStyles } from '~src/authentication/styles/AllUserIdentitiesSubspaceStyles';
import { Formatters, Messages } from '~src/codegen/Localize';
import { BreadcrumbStateInfo } from '~src/spacesPlatform/components/breadcrumbs/BreadcrumbStateInfo';
import { ConnectedExplorer } from '~src/spacesPlatform/components/Explorer/ConnectedExplorer';
import { ContentFiltersKeys } from '~src/spacesPlatform/models/ContentFilters';
import { SpaceContainerStyles } from '~src/spacesPlatform/style/SpaceContainerStyles';

export interface AllUserIdentitiesSubspaceStateProps {
  readonly isBusyFetchingItems: boolean;
  readonly numberOfFetchedUsers: number;
  readonly subspaceHref: string;
}

export interface AllUserIdentitiesSubspaceDispatchProps {
  readonly requestSpaceContents: () => void;
  readonly updateBreadcrumbs: (breadcrumbs: ReadonlyArray<BreadcrumbStateInfo>) => void;
}

type AllUserIdentitiesSubspaceProps = AllUserIdentitiesSubspaceStateProps & AllUserIdentitiesSubspaceDispatchProps;

interface AllUserIdentitiesSubspaceState {
  totalFailuresByType: Map<IdentityMigrationFailureType, number>;
  totalAcknowledgedFailures: number;
  totalSystemUsers: number;
  totalSystemUsersWithConflicts: number;
  totalSystemUsersWithIdentities: number;
}

function enterKeyListener(action: () => void) {
  return (event: React.KeyboardEvent<HTMLSpanElement>) => {
    if (event.keyCode === Keys.Enter) {
      action();
    }
  };
}

export class AllUserIdentitiesSubspace extends React.PureComponent<AllUserIdentitiesSubspaceProps, AllUserIdentitiesSubspaceState> {

  public constructor(props: AllUserIdentitiesSubspaceProps) {
    super(props);
    this.state = {
      totalAcknowledgedFailures: 0,
      totalFailuresByType: new Map(),
      totalSystemUsers: 0,
      totalSystemUsersWithConflicts: 0,
      totalSystemUsersWithIdentities: 0
    };
  }

  public componentDidMount() {
    this.props.updateBreadcrumbs([{
      id: this.props.subspaceHref,
      text: Messages.identityMigration(),
      href: this.props.subspaceHref,
      testId: 'all-user-identities-link'
    }]);
    BrowserTitleService.setTitleWithPlaceEntityName({ placeEntityName: Messages.identityMigrationTitle() });

    this.props.requestSpaceContents();

    this.updateTotals();
  }

  public componentDidUpdate(prevProps: AllUserIdentitiesSubspaceProps) {
    // After fetching new items update the totals.
    if (!this.props.isBusyFetchingItems && prevProps.isBusyFetchingItems) {
      this.updateTotals();
    }
  }

  private updateTotals() {
    UserIdentityConflictsService.fetchUserIdentityConflictTotals().then((result: GetUserIdentityConflictTotalsResult) => {
      this.setState({
        totalAcknowledgedFailures: result.totalAcknowledgedFailures,
        totalFailuresByType: new Map<IdentityMigrationFailureType, number>(Object.entries(result.totalFailuresByType) as any)
      });
    });
    UserIdentityConflictsService.fetchUserIdentityMigrationProgress().then((result: GetUserIdentityMigrationProgressResult) => {
      this.setState({
        totalSystemUsers: result.totalSystemUsers,
        totalSystemUsersWithConflicts: result.totalSystemUsersWithConflicts,
        totalSystemUsersWithIdentities: result.totalSystemUsersWithIdentities
      });
    });
  }

  public render(): JSX.Element {
    return (
      <div className={SpaceContainerStyles.SpaceContainer}>
        {this.getSubspaceHeader()}
        <div className={SpaceContainerStyles.SubplaceArea}>
          <ConnectedExplorer
            supportedDisplayModes={['list']}
            toolbarProps={{
              supportFilterPanel: true,
              toolbarVisibility: 'enabled',
              // TODO - 1179234 Add the content actions for identity conflicts.
              // toolbarContentSpecificControls: { beginning: this.addUserButtonOrMenu() }
            }}
            emptyStateProps={{
              primaryMessage: Messages.identityMigration_NoConflicts()
            }}
          />
        </div>
      </div>
    );
  }

  private getSubspaceHeader() {
    return <div className={AllUserIdentitiesSubspaceStyles.MastheadArea}>
      {this.renderSectionTitle()}
      {this.renderDescription()}
      {this.renderProgressBar()}
      {this.renderFailureTypeHeader()}
      {this.renderFailureTypeButtons()}
    </div>;
  }

  private renderSectionTitle() {
    return <div className={AllUserIdentitiesSubspaceStyles.SpaceHeaderContent}>
      <span className={AllUserIdentitiesSubspaceStyles.SpaceSubHeaderText}>
        {Messages.identityMigration_SusbpaceHeader()}
      </span>
      <RescheduleIdentityMigrationJobButton/>
    </div>;
  }

  private renderDescription() {
    return <div className={AllUserIdentitiesSubspaceStyles.SpaceHeaderContent}>
      {Messages.identityMigration_Description()}
      &nbsp;
      <TextLinkWidget
        kind = {TextLinkKind.Standalone}
        testId='identity-migration-learn-more'
        href={HelpService.getHelpUrlByCategory('server-identity-migration')}
        target={'_blank'}>
          {Messages.identityMigration_Learn_More()}
      </TextLinkWidget>
    </div>;
  }

  private renderProgressBar() {
    const onFinishedState = this.state.totalSystemUsersWithIdentities + this.state.totalAcknowledgedFailures;
    const currentProgressRatio = onFinishedState / this.state.totalSystemUsers || 0;
    const currentProgressPercentage = Math.floor(currentProgressRatio * 100);

    // The localization api wants a ratio, but we want to send the rounded number, and not the original one.
    // Sending the original ratio will render 99.9% completion as 100%, which is misleading.
    const currentProgressPercentageForLocalization = currentProgressPercentage / 100;
    const progressStatusMessageArgs = {
      MIGRATED: Formatters.formatCount(onFinishedState),
      TOTAL: Formatters.formatCount(this.state.totalSystemUsers)
    };

    return <div className={AllUserIdentitiesSubspaceStyles.SpaceHeaderContent}>
      <div
        aria-valuenow={currentProgressPercentage}
        aria-valuemin={0}
        aria-valuemax={100}
        className={AllUserIdentitiesSubspaceStyles.ProgressBar}
        role='progressbar'
      >
        <span className={AllUserIdentitiesSubspaceStyles.ProgressBarBackgroundFill}>
          <div
            className={AllUserIdentitiesSubspaceStyles.ProgressBarForegroundFill}
            data-tb-test-id={'MIGRATION_PROGRESS_BAR'}
            style={{ width: currentProgressPercentage + '%' }}
          >
          </div>
        </span>
        <div data-tb-test-id={'MIGRATION_PROGRESS_RATIO'}>
          {Messages.identityMigration_ProgressStatus(progressStatusMessageArgs)}
        </div>
        <span
          className={AllUserIdentitiesSubspaceStyles.ProgressNumber}
          data-tb-test-id={'MIGRATION_PROGRESS_PERCENTAGE'}
        >
          {Messages.identityMigration_Progress({PERCENTAGE: Formatters.formatPercent(currentProgressPercentageForLocalization)})}
        </span>
      </div>
    </div>;
  }

  private renderFailureTypeToolTip = (): JSX.Element => {
    return <div>
      <span className={AllUserIdentitiesSubspaceStyles.ToolTipHeaderText}>{Messages.identityMigration_FailureType_NonExistent()}</span>
      <span className={AllUserIdentitiesSubspaceStyles.ToolTipContent}>{Messages.identityMigration_FailureTypeDesc_NonExistent()}</span>
      <span className={AllUserIdentitiesSubspaceStyles.ToolTipHeaderText}>{Messages.identityMigration_FailureType_Ambiguous()}</span>
      <span className={AllUserIdentitiesSubspaceStyles.ToolTipContent}>{Messages.identityMigration_FailureTypeDesc_Ambiguous()}</span>
      <span className={AllUserIdentitiesSubspaceStyles.ToolTipHeaderText}>{Messages.identityMigration_FailureType_Duplicate()}</span>
      <span className={AllUserIdentitiesSubspaceStyles.ToolTipContent}>{Messages.identityMigration_FailureTypeDesc_Duplicate()}</span>
      <span className={AllUserIdentitiesSubspaceStyles.ToolTipHeaderText}>{Messages.identityMigration_FailureType_NonLocal()}</span>
      <span className={AllUserIdentitiesSubspaceStyles.ToolTipContent}>{Messages.identityMigration_FailureTypeDesc_NonLocal()}</span>
      <span className={AllUserIdentitiesSubspaceStyles.ToolTipHeaderText}>{Messages.identityMigration_FailureType_Unknown()}</span>
      <span className={AllUserIdentitiesSubspaceStyles.ToolTipContent}>{Messages.identityMigration_FailureTypeDesc_Unknown()}</span>
    </div>;
  };

  private renderFailureTypeHeader() {
    return <div className={AllUserIdentitiesSubspaceStyles.SpaceHeaderContent}>
      <div className={AllUserIdentitiesSubspaceStyles.SpaceSubHeaderText}>
        {Messages.identityMigration_FailureType_SectionHeader()}
        &nbsp;
        <Tooltip position={TooltipPosition.BottomRightCorner} getContent={this.renderFailureTypeToolTip}/>
      </div>
    </div>;
  }

  private renderFailureTypeButtons() {
    return <div className={AllUserIdentitiesSubspaceStyles.SpaceHeaderContent}>
      <div className={AllUserIdentitiesSubspaceStyles.FailureTypeButtonGroup}>
        {this.renderAllFailedGroup()}
        {Object.values(IdentityMigrationFailureType).map(x => this.renderFailureTypeGroup(x))}
        {this.renderAcknowledgedGroup()}
      </div>
      <div style={AllUserIdentitiesSubspaceStyles.FailureTypeBottomBar} />
    </div>;
  }

  private renderAllFailedGroup() {
    const totalFailed: number = Array.from(this.state.totalFailuresByType.values()).reduce((acc, val) => acc + val, 0);
    const isSelectedOption = this.getFailureTypeFiler() === undefined && !this.isFilteringByAcknowledged();
    return this.renderFailureType(Messages.identityMigration_FailureTypeGroup_AllFailed(), totalFailed, isSelectedOption,
      () => this.filterContentByFailureType());
  }

  private renderFailureTypeGroup(failureType: IdentityMigrationFailureType) {
    const totalFailed: number = this.state.totalFailuresByType.get(failureType) || 0;
    const isSelectedOption = this.getFailureTypeFiler() === failureType;
    return this.renderFailureType(localizeIdentityMigrationFailureGroupHeader(failureType), totalFailed, isSelectedOption,
      () => this.filterContentByFailureType(failureType));
  }

  private renderAcknowledgedGroup() {
    const isSelectedOption = this.isFilteringByAcknowledged();
    return this.renderFailureType(Messages.identityMigration_FailureTypeGroup_Acknowledged(),
      this.state.totalAcknowledgedFailures, isSelectedOption, () => this.filterContentByAcknowledged(!isSelectedOption));
  }

  private renderFailureType(label: string, count: number, isSelectedOption: boolean, onClick: () => void) {
    const itemLabelStyle = isSelectedOption ?
      AllUserIdentitiesSubspaceStyles.FailureTypeItemLabelSelectedOption :
      AllUserIdentitiesSubspaceStyles.FailureTypeItemLabel;

    const failureTypeClassName = isSelectedOption ?
      AllUserIdentitiesSubspaceStyles.FailureTypeItemSelectedOption :
      AllUserIdentitiesSubspaceStyles.FailureTypeItem;

    return <span className={failureTypeClassName} style={{ cursor: 'pointer' }} key={label} onClick={onClick} onKeyDown={enterKeyListener(onClick)} tabIndex={0} role={'button'}>
      <div style={itemLabelStyle}>
        {label}
      </div>
      <div style={AllUserIdentitiesSubspaceStyles.FailureTypeItemCount}>
        {count}
      </div>
      {isSelectedOption && <div style={AllUserIdentitiesSubspaceStyles.FailureTypeBottomBarSelectedOption} />}
    </span>;
  }

  private getFailureTypeFiler(): IdentityMigrationFailureType | undefined {
    const params = WindowLocationService.getHashQueryObject() as { [key: string]: string };
    return IdentityMigrationFailureType[params[ContentFiltersKeys.failureType] as keyof typeof IdentityMigrationFailureType];
  }

  private isFilteringByAcknowledged(): boolean {
    const params = WindowLocationService.getHashQueryObject() as { [key: string]: string };
    return Helpers.BooleanQueryParamRegex.test(params[ContentFiltersKeys.discardIdentity]);
  }

  private filterContentByFailureType(failureType?: IdentityMigrationFailureType) {
    WindowLocationService.deleteQueryParameter(ContentFiltersKeys.discardIdentity);
    if (failureType) {
      WindowLocationService.setHashQueryParameter(ContentFiltersKeys.failureType, failureType);
    } else {
      WindowLocationService.deleteQueryParameter(ContentFiltersKeys.failureType);
    }
  }

  private filterContentByAcknowledged(acknowledged: boolean) {
    WindowLocationService.deleteQueryParameter(ContentFiltersKeys.failureType);
    WindowLocationService.setHashQueryParameter(ContentFiltersKeys.discardIdentity, acknowledged.toString());
  }
}